import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { platformAdminApi } from '@/features/platform-admin/api/platformAdminApi';
import {
  getContactAgentTenantId,
  setContactAgentTenantId,
} from '../api/contactAgentScope';

type TenantOption = { id: string; name: string };

function toTenantOptions(list: Array<{ id: string; name: string }>): TenantOption[] {
  return list.map(item => ({ id: item.id, name: item.name }));
}

function pickTenantId(list: TenantOption[], stored: string | undefined): string {
  if (stored && list.some(item => item.id === stored)) {
    return stored;
  }
  return list[0]?.id ?? '';
}

export function useContactAgentTenant() {
  const user = useSelector(selectCurrentUser);
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState(
    isSuperAdmin ? getContactAgentTenantId() ?? '' : user?.tenantId ?? '',
  );
  const [loading, setLoading] = useState(isSuperAdmin);

  useEffect(() => {
    if (!isSuperAdmin) {
      setTenantId(user?.tenantId ?? '');
      setLoading(false);
      return;
    }

    void platformAdminApi
      .listTenants()
      .then(list => {
        const options = toTenantOptions(list);
        setTenants(options);
        const next = pickTenantId(options, getContactAgentTenantId());
        if (next) {
          setContactAgentTenantId(next);
          setTenantId(next);
        }
      })
      .catch(() => {
        setTenants([]);
      })
      .finally(() => setLoading(false));
  }, [isSuperAdmin, user?.tenantId]);

  const selectTenant = (id: string) => {
    if (isSuperAdmin && tenants.length > 0 && !tenants.some(item => item.id === id)) {
      return;
    }
    setContactAgentTenantId(id);
    setTenantId(id);
  };

  return {
    tenantId,
    tenants,
    isSuperAdmin,
    loading,
    ready: Boolean(tenantId) && !loading,
    selectTenant,
  };
}
